import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LISTS = ["leie", "sam", "state_lists"];
const LIST_LABELS = { leie: "OIG LEIE", sam: "SAM.gov", state_lists: "State lists" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

const emptyRow = () => ({ name: "", role: "", leie: "CLEAR", sam: "CLEAR", state_lists: "CLEAR", result: "CLEAR", evidence: "" });

export default function ScreeningLog({ active }) {
  const defaultMonth = () => new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(defaultMonth());
  const [rows, setRows] = useState([emptyRow()]);
  const [screenedBy, setScreenedBy] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [logs, setLogs] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(() => {
    axios.get(`${API}/screening`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, []);
  useEffect(() => { if (active === "a8") fetchLogs(); }, [active, fetchLogs]);

  const setRow = (i, field, value) =>
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [field]: value };
      const anyMatch = LISTS.some((l) => next[l] === "MATCH");
      if (LISTS.includes(field)) next.result = anyMatch ? "POTENTIAL_MATCH" : "CLEAR";
      return next;
    }));

  const canSubmit = rows.length > 0 && rows.every((r) => r.name.trim() && (r.result === "CLEAR" || r.evidence.trim())) &&
    screenedBy.trim() && signature.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/screening`, { month, rows, screened_by: screenedBy, signature });
      toast.success(`Exclusion screening ${month} attested — ${rows.length} name${rows.length !== 1 ? "s" : ""} screened`);
      setRows([emptyRow()]); setScreenedBy(""); setSignature("");
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record screening");
    }
    setSubmitting(false);
  };

  return (
    <section className={`chapter utility${active === "a8" ? " active" : ""}`} id="a8">
      <div className="eyebrow">Working record · Appendix A-8 · executes ECH-SOP-5.1</div>
      <h1>Monthly Exclusion Screening Log</h1>
      <p className="lede">Every name, every month, every list — with proof. Due by the 5th. Any list MATCH requires identity verification (DOB/NPI/license) and an evidence note; a confirmed exclusion means immediate removal from scheduling and billing, and a CAP.</p>

      <div className="formcard" data-testid="screening-form">
        <div className="formcard-title">Screening for month</div>
        <div className="month-bar" style={{ marginBottom: 14 }}>
          <input type="month" className="inp month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="scr-month" />
          <button className="btn ghost" onClick={() => setRows((p) => [...p, emptyRow()])} data-testid="scr-add-row">+ Add name</button>
        </div>
        <table className="mform-table">
          <tbody>
            <tr><th>Name</th><th style={{ width: 140 }}>Role</th>{LISTS.map((l) => <th key={l} style={{ width: 100 }}>{LIST_LABELS[l]}</th>)}<th style={{ width: 130 }}>Result</th><th style={{ width: 200 }}>Evidence / verification note</th><th style={{ width: 40 }} /></tr>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><input className="inp" value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} placeholder="Full name / entity" data-testid={`scr-name-${i}`} /></td>
                <td><input className="inp" value={r.role} onChange={(e) => setRow(i, "role", e.target.value)} placeholder="Assist / member / vendor" data-testid={`scr-role-${i}`} /></td>
                {LISTS.map((l) => (
                  <td key={l}>
                    <button type="button" className={`pf-btn ${r[l] === "CLEAR" ? "pass on" : "fail on"}`} style={{ width: "100%" }}
                      onClick={() => setRow(i, l, r[l] === "CLEAR" ? "MATCH" : "CLEAR")} data-testid={`scr-${l}-${i}`}>{r[l]}</button>
                  </td>
                ))}
                <td>
                  {r.result === "CLEAR" ? (
                    <span className="vr-pill cleared">CLEAR</span>
                  ) : (
                    <select className="inp" value={r.result} onChange={(e) => setRow(i, "result", e.target.value)} data-testid={`scr-result-${i}`} style={{ fontSize: 12 }}>
                      <option value="POTENTIAL_MATCH">POTENTIAL MATCH</option>
                      <option value="CONFIRMED_EXCLUSION">CONFIRMED EXCLUSION</option>
                    </select>
                  )}
                </td>
                <td><input className="inp" value={r.evidence} onChange={(e) => setRow(i, "evidence", e.target.value)} placeholder={r.result === "CLEAR" ? "screenshot/export filed" : "required — identity check"} data-testid={`scr-evidence-${i}`} /></td>
                <td>{rows.length > 1 && <button className="vr-link" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}>×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.some((r) => r.result === "CONFIRMED_EXCLUSION") && (
          <div className="pc-federal-warn" data-testid="scr-exclusion-warn">CONFIRMED EXCLUSION — remove from scheduling and billing immediately, notify the Compliance Officer, assess claims exposure with counsel, and open a CAP (SOP 5.1 step 4).</div>
        )}
        <div className="attest-row" style={{ marginTop: 10 }}>
          <input className="inp" placeholder="Screened by" value={screenedBy} onChange={(e) => setScreenedBy(e.target.value)} data-testid="scr-by" style={{ maxWidth: 220 }} />
          <input className="inp sig" placeholder="Type name as signature" value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="scr-signature" style={{ maxWidth: 240 }} />
          <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="scr-submit">Attest screening for {month}</button>
        </div>
      </div>

      <h2>Screening history</h2>
      <table data-testid="screening-history">
        <tbody>
          <tr><th>Month</th><th>Names screened</th><th>Matches</th><th>Screened by</th><th>Attested</th><th /></tr>
          {logs === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : logs.length === 0 ? (
            <tr><td colSpan={6}>No screenings recorded yet.</td></tr>
          ) : (
            logs.map((l) => (
              <React.Fragment key={l.id}>
                <tr>
                  <td className="q">{l.month}</td>
                  <td>{l.names_screened}</td>
                  <td>{l.matches > 0 ? <span className="vr-pill declined">{l.matches}</span> : <span className="vr-pill cleared">0</span>}</td>
                  <td>{l.screened_by}</td>
                  <td>{fmt(l.attested_at)}</td>
                  <td><button className="vr-link" onClick={() => setExpanded(expanded === l.id ? null : l.id)} data-testid={`scr-expand-${l.month}`}>{expanded === l.id ? "Hide" : "View names"}</button></td>
                </tr>
                {expanded === l.id && (
                  <tr><td colSpan={6}>
                    <table className="mform-table" style={{ margin: 0 }}>
                      <tbody>
                        <tr><th>Name</th><th>Role</th><th>LEIE</th><th>SAM</th><th>State</th><th>Result</th><th>Evidence</th></tr>
                        {l.rows.map((r, i) => (
                          <tr key={i}><td>{r.name}</td><td>{r.role}</td><td>{r.leie}</td><td>{r.sam}</td><td>{r.state_lists}</td><td>{r.result.replace("_", " ")}</td><td>{r.evidence || "—"}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </td></tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Completion attests in the MOCR (question C-1). Screening evidence file retains 10 years.</p>
    </section>
  );
}
